import { ButtonLink, Stack, StackItem } from '@asgard/ui-library';
import { useUser } from '@auth0/nextjs-auth0/client';
import { faEmptySet } from '@fortawesome/pro-light-svg-icons';
import {
  faBellPlus,
  faSpinnerThird,
  faTrash,
  faUserAstronaut
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/shared/Button.styled';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { FetchError } from 'model';
import { ManfrediteOfferAlert } from 'model/model-manfredite-offer-alert';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import OfferAlertService from 'services/service-offer-alert';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getJobOfferAlertName } from 'utils/job-offers-alerts';
import { getUrlParamsFromJobOffersFilter } from 'utils/job-offers-filter';
import { getLocale } from 'utils/localize.utils';
import ConfirmModal from '../../../../../components/modals/ConfirmModal';
import Modal from '../../../../../components/shared/Modal';
import { UNSUSCRIBE_ALERT_PARAM } from '../JobOffersListComponent.constants';
import {
  StyledEmpty,
  StyledEmptyIcon,
  StyledEmptyTitle,
  StyledOfferAlertItem,
  StyledOfferAlertItemContent
} from './OfferAlertModal.styled';
import { OfferAlertModalProps } from './OfferAlertModal.types';

export const OfferAlertModal: React.FC<OfferAlertModalProps> = ({
  offerAlerts,
  techs,
  techRoles,
  setOfferAlerts,
  close,
  isOpened,
  unsuscribeActionOfferAlert,
  onCreateAlertClick,
  userCanCreateAlerts
}) => {
  const { t } = useI18n('offer-alert-modal');
  const { user } = useUser();
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();
  const [isRemoving, setIsRemoving] = useState<string | undefined>();
  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);

  const toDeleteAlert = useRef<ManfrediteOfferAlert | undefined>();

  useEffect(() => {
    if (unsuscribeActionOfferAlert) {
      setIsConfirmModalOpened(true);
    }
  }, [unsuscribeActionOfferAlert]);

  const handleDeleteClick = async (): Promise<void> => {
    try {
      const alertId = toDeleteAlert.current?.id || unsuscribeActionOfferAlert;

      if (alertId) {
        setIsRemoving(alertId);

        await OfferAlertService.remove(alertId);

        setOfferAlerts(offerAlerts.filter((alert) => alertId !== alert.id));

        setIsRemoving(undefined);
        toDeleteAlert.current = undefined;
        addSuccessToast(t('offerAlertModal.messages.success.delete'));
      }
    } catch (ex) {
      if (ex instanceof FetchError && ex.statusCode >= 500) {
        console.error(
          `Could not delete Manfredite alert with ID: ${
            toDeleteAlert.current?.id || unsuscribeActionOfferAlert
          }. Exception: ${ex}`,
          ex
        );
      }

      addErrorToast(t('offerAlertModal.messages.error.delete'));
      toDeleteAlert.current = undefined;
    }
  };

  return (
    <>
      <ReactModal
        isOpen={isOpened}
        contentLabel={t('offerAlertModal.title')}
        onRequestClose={close}
      >
        <Modal onClose={close} title={t('offerAlertModal.title')}>
          {offerAlerts.length <= 0 ? (
            <StyledEmpty>
              <StyledEmptyIcon>
                <FontAwesomeIcon size="4x" icon={faEmptySet} />
              </StyledEmptyIcon>
              <Stack direction="column" gap="xl">
                <StyledEmptyTitle>
                  {t('offerAlertModal.messages.info.no-results.title')}
                </StyledEmptyTitle>
                <p>{t('offerAlertModal.messages.info.no-results.text')}</p>

                <StackItem>
                  {user ? (
                    <Button
                      sizes="xs"
                      variant="primary"
                      onClick={onCreateAlertClick}
                      disabled={!userCanCreateAlerts}
                    >
                      <FontAwesomeIcon icon={faBellPlus} />
                      <span>
                        {t('offerAlertModal.messages.info.no-results.cta')}
                      </span>
                    </Button>
                  ) : (
                    <ButtonLink
                      size="xs"
                      variant="hard"
                      schema="neutral"
                      label={t(
                        'offerAlertModal.messages.info.no-results.register'
                      )}
                      startIcon={faUserAstronaut}
                      href={`${getAppUrl(
                        APP_URLS.join,
                        locale
                      )}?returnUrl=${getAppUrl(APP_URLS.jobOffers, locale)}`}
                    />
                  )}
                </StackItem>
              </Stack>
            </StyledEmpty>
          ) : (
            <div>
              {offerAlerts.map((alertItem) => (
                <StyledOfferAlertItem key={alertItem.id}>
                  <StyledOfferAlertItemContent>
                    <a
                      href={`${getAppUrl(
                        APP_URLS.jobOffers,
                        locale
                      )}?${getUrlParamsFromJobOffersFilter(
                        alertItem.filter,
                        getLocale(locale),
                        [UNSUSCRIBE_ALERT_PARAM]
                      )}`}
                    >
                      {getJobOfferAlertName({
                        alert: alertItem,
                        techs,
                        techRoles,
                        t,
                        locale
                      })}
                    </a>
                  </StyledOfferAlertItemContent>
                  <Button
                    isIcon
                    sizes="sm"
                    isLoading={isRemoving === alertItem.id}
                    variant="tertiaryNegative"
                    onClick={() => {
                      toDeleteAlert.current = alertItem;
                      setIsConfirmModalOpened(true);
                    }}
                  >
                    {isRemoving !== alertItem.id && (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                    {isRemoving === alertItem.id && (
                      <FontAwesomeIcon icon={faSpinnerThird} spin />
                    )}
                  </Button>
                </StyledOfferAlertItem>
              ))}
            </div>
          )}
        </Modal>
      </ReactModal>

      <ConfirmModal
        isOpen={isConfirmModalOpened}
        close={() => setIsConfirmModalOpened(false)}
        action={handleDeleteClick}
        title={t('offerAlertModal.confirm-delete.title', {
          alert: toDeleteAlert.current
            ? getJobOfferAlertName({
                alert: toDeleteAlert.current,
                techs,
                techRoles,
                t,
                locale,
                criteriaCount: 2
              })
                .charAt(0)
                .toUpperCase() +
              getJobOfferAlertName({
                alert: toDeleteAlert.current,
                techs,
                techRoles,
                t,
                locale,
                criteriaCount: 2
              }).slice(1)
            : ''
        })}
        text={t('offerAlertModal.confirm-delete.text')}
        confirmText={t('offerAlertModal.confirm-delete.action-confirm')}
        cancelText={t('offerAlertModal.confirm-delete.action-cancel')}
      />
    </>
  );
};
export default OfferAlertModal;
